/**
 * /admin/checkout-failures — operator dashboard for every Stripe checkout
 * failure (client-side OR server-side) so we never need a rider screenshot
 * to debug.
 *
 * Data feed: GET /api/admin/checkout-failures
 *   - failures: most-recent records (capped)
 *   - counts.today / counts.week / counts.total
 *   - top_codes: top 8 error codes in the last 7 days
 *   - failure_rate: failures_week / attempts_week (rounded 1dp)
 *
 * Each row is expandable to show the full diagnostic struct (every Stripe
 * ID + status). Click "Open in Stripe" to deep-link to the underlying
 * Subscription / Invoice / PaymentIntent in the Stripe Dashboard.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, RefreshCw, AlertTriangle, ChevronDown, ChevronUp, ExternalLink, Server, Smartphone } from "lucide-react";
import { toast } from "sonner";

import { api, formatApiError } from "@/shared/lib/api";

export default function AdminCheckoutFailures() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(() => new Set());

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get("/admin/checkout-failures", { params: { limit: 200 } });
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Could not load checkout failures");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (id) => setExpanded((s) => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const counts = data?.counts || { today: 0, week: 0, total: 0 };
  const topCodes = data?.top_codes || [];
  const failures = data?.failures || [];

  return (
    <>
      <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6" data-testid="admin-checkout-failures-page">
        {/* Header */}
        <div className="flex items-end justify-between gap-4 mb-6">
          <div>
            <div className="text-[10px] tracking-[0.26em] uppercase font-medium text-[#ed6500] mb-1">Stripe diagnostics</div>
            <h1 className="font-serif text-3xl text-[#0A1128] leading-tight">Checkout failures</h1>
            <p className="text-[13px] text-[#515E7B] mt-1">
              Every failed Stripe confirm — client-side and server-side — persisted with the full diagnostic struct. No screenshots needed.
            </p>
          </div>
          <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-white border hairline text-[10px] tracking-[0.22em] uppercase font-semibold text-[#0A1128] hover:border-[#ed6500] disabled:opacity-50 transition-colors"
            data-testid="acf-refresh"
          >
            {refreshing ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} strokeWidth={1.8} />}
            Refresh
          </button>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="acf-kpis">
          <Kpi label="Failed today" value={counts.today} testId="acf-kpi-today" />
          <Kpi label="Failed this week" value={counts.week} testId="acf-kpi-week" />
          <Kpi label="Failure rate (7d)" value={`${data?.failure_rate ?? 0}%`} sub={`${data?.attempts_week ?? 0} attempts`} testId="acf-kpi-rate" />
          <Kpi label="All-time" value={counts.total} testId="acf-kpi-total" />
        </div>

        {/* Top codes */}
        {topCodes.length > 0 && (
          <div className="rounded-2xl bg-white border hairline p-4 mb-6" data-testid="acf-top-codes">
            <div className="text-[10px] tracking-[0.26em] uppercase font-semibold text-[#515E7B] mb-3">Top error codes · last 7 days</div>
            <div className="flex flex-wrap gap-2">
              {topCodes.map((c) => (
                <span
                  key={c.code}
                  className="inline-flex items-center gap-2 px-3 h-8 rounded-full bg-[#ed6500]/10 border border-[#ed6500]/30 text-[12px] text-[#0A1128] font-mono"
                  data-testid={`acf-top-code-${c.code}`}
                >
                  <span className="font-medium">{c.code}</span>
                  <span className="text-[#ed6500] font-semibold">{c.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* List */}
        {loading ? (
          <div className="rounded-2xl bg-white border hairline p-12 text-center text-[13px] text-[#515E7B]" data-testid="acf-loading">
            <Loader2 size={20} className="mx-auto mb-3 animate-spin text-[#ed6500]" />
            Loading checkout failures…
          </div>
        ) : failures.length === 0 ? (
          <div className="rounded-2xl bg-white border hairline p-12 text-center" data-testid="acf-empty">
            <div className="font-serif text-[20px] text-[#0A1128] mb-1">No failures recorded</div>
            <div className="text-[13px] text-[#515E7B]">
              Every successful checkout stays out of this list. As soon as a rider hits a Stripe confirm error, they appear here within seconds.
            </div>
          </div>
        ) : (
          <ul className="space-y-2" data-testid="acf-list">
            {failures.map((f) => (
              <FailureRow
                key={f.id}
                row={f}
                expanded={expanded.has(f.id)}
                onToggle={() => toggle(f.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </>
  );
}

/* ============================  bits  ============================ */

function Kpi({ label, value, sub, testId }) {
  return (
    <div className="rounded-2xl bg-white border hairline p-4" data-testid={testId}>
      <div className="text-[10px] tracking-[0.26em] uppercase font-semibold text-[#515E7B] mb-1">{label}</div>
      <div className="font-serif text-[28px] leading-none text-[#0A1128]">{value}</div>
      {sub && <div className="text-[11px] text-[#515E7B] mt-1">{sub}</div>}
    </div>
  );
}

function FailureRow({ row, expanded, onToggle }) {
  const ts = useMemo(() => formatTs(row.created_at), [row.created_at]);
  const isClient = row.source === "client";
  return (
    <li className="rounded-2xl bg-white border hairline overflow-hidden" data-testid={`acf-row-${row.id}`}>
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-[#F9F7F4] transition-colors"
        data-testid={`acf-row-toggle-${row.id}`}
      >
        <div className="shrink-0 w-9 h-9 rounded-full bg-[#ed6500]/10 flex items-center justify-center text-[#ed6500]">
          <AlertTriangle size={16} strokeWidth={1.8} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[13px] font-medium text-[#0A1128] truncate" data-testid={`acf-row-email-${row.id}`}>
              {row.email || "—"}
            </span>
            {row.error_code && (
              <span className="inline-flex items-center px-2 h-5 rounded-full bg-[#ed6500]/10 text-[10px] font-mono text-[#ed6500]" data-testid={`acf-row-code-${row.id}`}>
                {row.error_code}
              </span>
            )}
            {row.decline_code && (
              <span className="inline-flex items-center px-2 h-5 rounded-full bg-red-100 text-[10px] font-mono text-red-700">
                {row.decline_code}
              </span>
            )}
            <span
              className="inline-flex items-center gap-1 text-[10px] tracking-[0.18em] uppercase text-[#515E7B]"
              title={isClient ? "Captured in the browser" : "Captured on the server"}
            >
              {isClient ? <Smartphone size={10} /> : <Server size={10} />}
              {row.source || "—"}
            </span>
          </div>
          <div className="text-[12px] text-[#515E7B] mt-0.5 truncate">
            <span className="font-mono">[{row.stage || "—"}]</span> {row.message || "—"}
          </div>
        </div>
        <div className="shrink-0 text-right">
          <div className="text-[10px] tracking-[0.18em] uppercase text-[#515E7B]" data-testid={`acf-row-ts-${row.id}`}>{ts}</div>
        </div>
        <div className="shrink-0 ml-1 text-[#515E7B]">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 border-t hairline bg-[#F9F7F4]/50" data-testid={`acf-row-detail-${row.id}`}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-[12px] font-mono break-all">
            <Field label="Stage" value={row.stage} />
            <Field label="Source" value={row.source} />
            <Field label="HTTP status" value={row.http_status} />
            <Field label="Type" value={row.type} />
            <Field label="Error code" value={row.error_code} />
            <Field label="Decline code" value={row.decline_code} />
            <Field label="Stripe request" value={row.stripe_request_id} />
            <Field label="Intent kind" value={row.intent_kind} />
            <Field label="Intent ID" value={row.intent_id} />
            <Field label="SetupIntent ID" value={row.setup_intent_id} />
            <Field label="SetupIntent status" value={row.setup_intent_status} />
            <Field label="Subscription ID" value={row.subscription_id} link={subLink(row.subscription_id)} />
            <Field label="Subscription status" value={row.subscription_status} />
            <Field label="Invoice ID" value={row.invoice_id} link={invLink(row.invoice_id)} />
            <Field label="PaymentIntent ID" value={row.payment_intent_id} link={piLink(row.payment_intent_id)} />
            <Field label="PaymentIntent status" value={row.payment_intent_status} />
            <Field label="Payment method" value={row.payment_method_id} />
            <Field label="Route ID" value={row.route_id} />
            <Field label="User ID" value={row.user_id} />
            <Field label="Created" value={row.created_at} />
          </div>
          {row.message && (
            <div className="mt-3 text-[12px] text-[#0A1128] bg-white rounded-lg border hairline p-3" data-testid={`acf-row-message-${row.id}`}>
              <div className="text-[10px] tracking-[0.26em] uppercase text-[#515E7B] mb-1">Message</div>
              {row.message}
            </div>
          )}
          {row.raw_payload && (
            <details className="mt-3" data-testid={`acf-row-raw-${row.id}`}>
              <summary className="cursor-pointer text-[10px] tracking-[0.26em] uppercase text-[#515E7B] hover:text-[#0A1128]">Show raw payload</summary>
              <pre className="mt-2 text-[11px] font-mono bg-white border hairline rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all max-h-96">
{safeStringify(row.raw_payload)}
              </pre>
            </details>
          )}
        </div>
      )}
    </li>
  );
}

function Field({ label, value, link }) {
  if (value === null || value === undefined || value === "") return null;
  return (
    <>
      <div className="text-[#515E7B]">{label}</div>
      <div className="text-[#0A1128] flex items-center gap-1.5">
        <span className="truncate">{String(value)}</span>
        {link && (
          <a
            href={link}
            target="_blank"
            rel="noreferrer"
            className="shrink-0 inline-flex items-center text-[#ed6500] hover:text-[#0A1128]"
            title="Open in Stripe Dashboard"
          >
            <ExternalLink size={11} strokeWidth={1.8} />
          </a>
        )}
      </div>
    </>
  );
}

function formatTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const date = d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
    return `${date} ${time}`;
  } catch (_e) { return iso; }
}

function safeStringify(v) {
  try { return typeof v === "string" ? v : JSON.stringify(v, null, 2); }
  catch (_e) { return String(v); }
}

const subLink = (id) => id ? `https://dashboard.stripe.com/subscriptions/${id}` : null;
const invLink = (id) => id ? `https://dashboard.stripe.com/invoices/${id}` : null;
const piLink  = (id) => id ? `https://dashboard.stripe.com/payments/${id}`   : null;
